import { z } from "zod";

export const billingSourceSchema = z.enum(["LOCATION", "ORGANIZATION"]);
export type BillingSource = z.infer<typeof billingSourceSchema>;

export const employmentTypeSchema = z.enum(["W2", "F1099"]);
export type EmploymentType = z.infer<typeof employmentTypeSchema>;

export const gigAttachmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  provider: z.string(),
  provider_identifier: z.string(),
  created_ts: z.coerce.date(),
  status: z.enum(["available", "deleted", "pending", "rejected"]),
  resource_url: z.string().url(),
});
export type GigAttachment = z.infer<typeof gigAttachmentSchema>;

export const gigStatusSchema = z.enum([
  "UNAPPROVED",
  "CANCELLED",
  "POSTED",
  "CONFIRMED",
  "UNFILLED",
  "INCOMPLETE",
  "FAILED_PAYMENT",
  "COMPLETED",
]);
export const gigStatuses = gigStatusSchema.options;
/**
 * Schema for gig statuses.
 * UNAPPROVED: Posted from an unapproved location.
 * CANCELLED: Cancelled (whether finished or not).
 * POSTED: Hasn't finished, nobody confirmed.
 * CONFIRMED: Hasn't finished, all pros confirmed.
 * UNFILLED: Finished, nobody confirmed.
 * INCOMPLETE: Finished, confirmed, not completed.
 * FAILED_PAYMENT: Finished, confirmed, failed payment.
 * COMPLETED: Finished, confirmed, completed.
 */
export type GigStatus = z.infer<typeof gigStatusSchema>;

export const gigTypeSchema = z.enum(["MARKETPLACE", "PRIVATE_DIRECT_OFFER", "INTERVIEW", "NETWORK"]);
export const gigTypes = gigTypeSchema.options;
/**
 * Schema for gig types.
 * MARKETPLACE: A classic gig open to the gigpro Marketplace.
 * PRIVATE_DIRECT_OFFER: A private gig offered to a specific pro in the Marketplace.
 * INTERVIEW: A hire interview.
 * NETWORK: A gig posted to network Pros, not available in the Marketplace.
 */
export type GigType = z.infer<typeof gigTypeSchema>;

export const shiftBreakComplianceSchema = z.enum([
  "COMPLIANT",
  "NON_COMPLIANT",
  "NOT_APPLICABLE",
  "NO_START_END_TIME",
  "SHORT_SHIFT",
]);
export const shiftBreakFieldsSchema = z.object({
  id: z.string(),
  minutes: z.number().nullish(),
  // seconds are the whole seconds of the break, not just the seconds part of the minutes
  seconds: z.number().nullish(),
  start_time: z.coerce.date().nullish(),
  end_time: z.coerce.date().nullish(),
  compliance_status: shiftBreakComplianceSchema,
});
export const shiftBreakSchema = shiftBreakFieldsSchema.transform((data) => {
  const minutes = data.minutes ?? 0;
  const seconds = data.seconds ?? 0;

  const hoursFormatted = `${Math.floor(minutes / 60)}`.padStart(2, "0");
  const minutesFormatted = `${minutes % 60}`.padStart(2, "0");
  const secondsFormatted = `${seconds - minutes * 60}`.padStart(2, "0");

  return {
    ...data,
    minutes,
    seconds,
    // Format the duration as HH:MM:SS
    durationFormatted: `${hoursFormatted}:${minutesFormatted}:${secondsFormatted}`,
  };
});
export type ShiftBreak = z.infer<typeof shiftBreakSchema>;
